import { useQuery } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import {
  buildSportsbookKpiQueryParams,
  parseSportsbookKpiFilterMetadata,
  type SportsbookKpiFilterAvailability,
  type SportsbookKpiFilterOptions,
  type SportsbookKpiFilterValues,
} from "./sportsbookKpiFilters";

type StoredToken = { AccessToken?: string };

export type SportsbookKpiCardsFilters = SportsbookKpiFilterValues & {
  start_date: string;
  end_date: string;
};

export type SportsbookKpiCard = {
  key: string;
  value: number;
  previousValue: number;
  percentage: number;
  rate: boolean;
};

type RawKpiEntry = {
  value: number;
  previous_value: number;
  percent_change: number | null;
};

export type SportsbookKpiCardsResult = {
  cards: SportsbookKpiCard[];
  filterOptions: SportsbookKpiFilterOptions;
  selectedFilters: Required<SportsbookKpiFilterValues>;
  availableFilters: SportsbookKpiFilterAvailability;
};

const RATE_KEYS = new Set(["margin_pct"]);
export const CARD_ORDER = ["turnover", "pnl", "margin_pct", "total_bets", "avg_bet_amount", "total_users"];

export const fetchSportsbookKpiCards = async (
  filters: SportsbookKpiCardsFilters,
): Promise<SportsbookKpiCardsResult> => {
  const tokenString = localStorage.getItem("token");
  let token: StoredToken | null = null;
  try {
    token = tokenString ? JSON.parse(tokenString) : null;
  } catch {
    token = null;
  }
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);

  const response = await instance.get(API_ROUTES.SPORTSBOOK_KPI_CARDS, {
    params: buildSportsbookKpiQueryParams(filters),
  });

  const raw = response?.data?.data;
  const cards =
    raw && typeof raw === "object"
      ? CARD_ORDER
          .filter((key) => key in raw)
          .map((key) => {
            const entry = raw[key] as RawKpiEntry;
            return {
              key,
              value: entry.value ?? 0,
              previousValue: entry.previous_value ?? 0,
              percentage: entry.percent_change ?? 0,
              rate: RATE_KEYS.has(key),
            };
          })
      : [];

  const metadata = parseSportsbookKpiFilterMetadata(response?.data);
  return {
    cards,
    filterOptions: metadata.options,
    selectedFilters: metadata.selectedValues,
    availableFilters: metadata.available,
  };
};

export const useGetSportsbookKpiCards = (
  filters: SportsbookKpiCardsFilters,
) => {
  const { start_date, end_date, sport, league, bet_type, money_type } =
    filters;

  return useQuery<SportsbookKpiCardsResult, Error>({
    queryKey: [
      "SPORTSBOOK_KPI_CARDS",
      start_date,
      end_date,
      sport,
      league,
      bet_type?.join(","),
      money_type?.join(","),
    ],
    queryFn: () => fetchSportsbookKpiCards(filters),
    enabled: Boolean(start_date && end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
